import React, { useEffect, useRef, useState } from 'react';
import {
    DiagnosisCode,
    Medication,
    MedicationType,
    MedicalOpinion,
    MedicalOpinionFile,
} from '../../types/api';
import { ModalWrapper } from './ModalWrapper';
import { MedicalOpinionFilePreview } from '../medicalOpinion/MedicalOpinionFilePreview';
import { MedicalOpinionFileRow } from '../medicalOpinion/MedicalOpinionFileRow';
import { base64ToObjectUrl, downloadBase64File } from '../medicalOpinion/medicalOpinionFiles';
import { POPUP_STYLES } from '../../constants/popupStyles';
import {
    MEDICAL_OPINION_ACCEPTED_MIME as ACCEPTED_MIME,
    MEDICAL_OPINION_ACCEPT_ATTR as ACCEPT_ATTR,
    MEDICAL_OPINION_MAX_FILES as MAX_FILES,
    MEDICAL_OPINION_MAX_FILE_BYTES as MAX_FILE_BYTES,
} from '../../constants/medicalOpinion';
import { useMedicalOpinionTypes } from '../../hooks/useMedicalOpinionOptions';
import apiService from '../../services/api';
import { CallPatientConfModal } from './CallPatientConfModal';
import { getTextDirection } from '../../utils/textDirection';
import { displayPhoneNumber } from '../../utils/phoneUtils';

/**
 * The psychiatrist's drafting screen for a medical opinion (the Psychiatric Opinion Letter
 * task), combining the request view with the letter itself.
 *
 * The Opinion tab has two states, and the backend enforces both:
 *
 *   unlocked  fields editable, Save and Create PDF available, Update In Clicks refused
 *   locked    fields read-only, Update In Clicks and Unlock available, Save refused
 *
 * Create PDF is what locks it. The reason is the workflow around this screen: the doctor
 * generates the letter, uploads that file to Clicks *by hand*, and only then presses Update
 * In Clicks. Nothing regenerates on completion, so the letter the system keeps is the one
 * that was uploaded. Unlocking to edit means the PDF has to be generated again before the
 * request can be completed.
 */
interface MedicalOpinionLetterModalProps {
    isOpen: boolean;
    onClose: () => void;
    token: string;
    opinion: MedicalOpinion | null;
    patientName: string;
    patientTz: string;
    patientHmId: string;
    patientPhone: string;
    // Offered alongside the patient's own number in the call modal, when the caller has
    // them. Absent from a task row, present on the patient page.
    parent1Phone?: string;
    parent2Phone?: string;
    clinicName: string;
    // The letter is written by the doctor it is assigned to, decided by comparing this
    // against opinion.doctor_id, or by any admin (isAdmin below). Everyone else gets the
    // same screen read-only.
    currentUserId?: number;
    // Admins write the letter too - a secretary fixing a typo or generating the pdf for a
    // doctor who is away. The signature stays the assigned doctor's either way: the backend
    // reads it off the opinion row, never off the caller.
    isAdmin?: boolean;
    diagnosisCodes: DiagnosisCode[];
    medicationTypes: MedicationType[];
    getUserName?: (userId: number) => string;
    // Called after the request is completed, so the caller can drop the task row.
    onSuccess: () => void;
}

const formatDateTime = (timestamp?: number): string => {
    if (!timestamp) return '—';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '—';
    return `${date.toLocaleDateString('en-GB')} ${date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
    })}`;
};

const EMPTY_FORM = {
    summary_and_discussion: '',
    status: '',
    recommendations: '',
    diagnosis: [] as string[],
    diagnosis_text: '',
};

// Same shape the letter's pdf renders, so what the doctor reads here is what comes out.
const formatMedication = (medication: Medication, medicationTypes: MedicationType[]): string => {
    const name =
        medicationTypes.find((t) => t.id === medication.medication_type_id)?.name
        || medication.name
        || '';
    const parts: string[] = [name];

    if (medication.dosage && medication.unit) {
        parts.push(`${medication.dosage} ${medication.unit}`);
    } else if (medication.dosage) {
        parts.push(String(medication.dosage));
    } else if (medication.unit) {
        parts.push(medication.unit);
    }

    if (medication.amount && medication.amount_per) {
        parts.push(`${medication.amount}/${medication.amount_per}`);
    }
    if (medication.duration) {
        parts.push(String(medication.duration));
    }

    return parts.filter(Boolean).join(' X ');
};

export const MedicalOpinionLetterModal: React.FC<MedicalOpinionLetterModalProps> = ({
    isOpen,
    onClose,
    token,
    opinion,
    patientName,
    patientTz,
    patientHmId,
    patientPhone,
    parent1Phone,
    parent2Phone,
    clinicName,
    currentUserId,
    isAdmin = false,
    diagnosisCodes,
    medicationTypes,
    getUserName,
    onSuccess,
}) => {
    const { labels: opinionTypeLabels } = useMedicalOpinionTypes();

    // The same call flow as the patient page's phone icon: a small modal offering Phone or
    // WhatsApp over the numbers on record, rather than this icon deciding for the user.
    const [isCallModalOpen, setIsCallModalOpen] = useState(false);
    const [callMessage, setCallMessage] = useState<string | null>(null);


    const [activeTab, setActiveTab] = useState<'request' | 'opinion'>('opinion');

    const [formData, setFormData] = useState(EMPTY_FORM);
    const [medications, setMedications] = useState<Medication[]>([]);
    const [isLocked, setIsLocked] = useState(false);
    const [files, setFiles] = useState<MedicalOpinionFile[]>([]);

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [copied, setCopied] = useState<string | null>(null);

    // Both are inline confirmations rather than child modals: one guards the irreversible
    // completion, the other guards closing over unsaved text.
    const [isConfirmingComplete, setIsConfirmingComplete] = useState(false);
    const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);

    const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewType, setPreviewType] = useState<string | null>(null);
    const [previewName, setPreviewName] = useState<string | null>(null);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [deletingFileId, setDeletingFileId] = useState<string | null>(null);

    // Diagnosis picker state, mirroring PSYSummaryModal - the same picker, on the same pair
    // of fields, so a psychiatrist meets one control and not two.
    const [diagnosisSearch, setDiagnosisSearch] = useState('');
    const [showDiagnosisDropdown, setShowDiagnosisDropdown] = useState(false);
    const [editingNoteCode, setEditingNoteCode] = useState<string | null>(null);
    const [noteDraft, setNoteDraft] = useState('');

    // Revoked whenever the preview is replaced and on unmount, so selecting several files in
    // a row does not leak a Blob per click.
    const previewUrlRef = useRef<string | null>(null);
    const setPreview = (url: string | null) => {
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
        }
        previewUrlRef.current = url;
        setPreviewUrl(url);
    };

    const clearPreview = () => {
        setSelectedFileId(null);
        setPreview(null);
        setPreviewType(null);
        setPreviewName(null);
        setPreviewError(null);
    };

    // Load the letter each time a different request is opened. The content is seeded from
    // the patient's last psychiatric meeting until something has been saved against this
    // request, which the backend decides - the modal just renders what it is given.
    useEffect(() => {
        if (!isOpen || !opinion) return;

        let cancelled = false;
        setActiveTab('opinion');
        setFormData(EMPTY_FORM);
        setMedications([]);
        setFiles(opinion.files || []);
        setIsLocked(!!opinion.summary_locked);
        setHasUnsavedChanges(false);
        setError(null);
        setNotice(null);
        setIsConfirmingComplete(false);
        setIsConfirmingDiscard(false);
        setDiagnosisSearch('');
        setShowDiagnosisDropdown(false);
        setEditingNoteCode(null);
        setNoteDraft('');
        clearPreview();
        setIsLoading(true);

        (async () => {
            try {
                const data = await apiService.getMedicalOpinionPsyData({
                    token,
                    medical_opinion_id: opinion.id,
                });
                if (cancelled) return;
                setFormData({
                    summary_and_discussion: data.summary_and_discussion || '',
                    status: data.status || '',
                    recommendations: data.recommendations || '',
                    diagnosis: data.diagnosis
                        ? data.diagnosis.split(',').map((c) => c.trim()).filter(Boolean)
                        : [],
                    diagnosis_text: data.diagnosis_text || '',
                });
                setMedications(data.medications || []);
                setIsLocked(!!data.summary_locked);
            } catch (err: any) {
                if (cancelled) return;
                setError(
                    err?.response?.status === 403
                        ? 'You are not authorized to open this letter'
                        : 'Failed to load the letter'
                );
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, opinion?.id]);

    useEffect(() => {
        return () => {
            if (previewUrlRef.current) {
                URL.revokeObjectURL(previewUrlRef.current);
            }
        };
    }, []);

    if (!isOpen || !opinion) return null;

    // The letter is written by the doctor it is assigned to, or by an admin on their behalf
    // - the same pair the backend's _require_letter_editor accepts. The pdf carries the
    // assigned doctor's signature in both cases, since it is read off the opinion row.
    // A case manager, or a doctor looking at another doctor's request, gets the same screen
    // with no actions rather than an action that fails.
    const isAssignedDoctor = !!currentUserId && opinion.doctor_id === currentUserId;
    const canAct = (isAssignedDoctor || isAdmin) && opinion.status === 'in_progress';
    const isEditable = canAct && !isLocked;

    // A completed request is the finished document, not a draft, so it says so in the
    // caption - the same modal reached from the patient page's list is then obviously a
    // record rather than something still being written.
    const isCompleted = opinion.status === 'completed';
    const title = isCompleted ? 'Psychiatric Opinion - Completed' : 'Psychiatric Opinion Letter';

    const inputFiles = files.filter((f) => f.file_type === 'input');
    // The generated letter has a fixed slot of its own - there is at most one, and its
    // absence is information - while everything else the psychiatrist attaches is a list.
    const letterFile = files.find((f) => f.file_type === 'output_summary') || null;
    const otherFiles = files.filter((f) => f.file_type === 'output_other');
    const isPreviewOpen = selectedFileId !== null;

    // The Opinion tab carries the whole letter - three narrative fields, the diagnosis list,
    // medications and two file sections - so it gets a document's width. The Request tab is a
    // handful of short read-only fields and stays at the obligation modal's size.
    //
    // An inline width rather than a max-w class, because the modal is resizable: the browser's
    // resize grip writes width and height straight onto the element, and a max-w class would
    // cap the drag. React only rewrites a style property when its own value changes, so a size
    // the user dragged survives every re-render - and is deliberately replaced when the width
    // below changes, i.e. on switching tab or opening the preview.
    const modalWidth = activeTab === 'opinion'
        ? (isPreviewOpen ? '90rem' : '56rem')
        : (isPreviewOpen ? '82rem' : '42rem');

    const requestedByName =
        opinion.created_by === 0
            ? 'Patient (app)'
            : opinion.created_by_name
                || getUserName?.(opinion.created_by)
                || `User #${opinion.created_by}`;

    const doctorName = opinion.doctor_id
        ? opinion.doctor_name || getUserName?.(opinion.doctor_id) || `User #${opinion.doctor_id}`
        : '';

    const handleFormChange = (field: keyof typeof EMPTY_FORM, value: string | string[]) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setHasUnsavedChanges(true);
        setNotice(null);
    };

    const copy = async (label: string, value: string) => {
        if (!value) return;
        try {
            await navigator.clipboard.writeText(value);
            setCopied(label);
            setTimeout(() => setCopied((c) => (c === label ? null : c)), 1500);
        } catch {
            setError('Could not copy to clipboard');
        }
    };

    // --- diagnosis picker (same behaviour as the PSY summary modal) -----------------

    const parseDiagnosisTextMap = (raw: string): Record<string, string> => {
        try {
            const parsed = JSON.parse(raw || '{}');
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                return parsed as Record<string, string>;
            }
        } catch { /* fall through */ }
        return {};
    };

    const serializeNotes = (codes: string[], map: Record<string, string>): string => {
        const ordered: Record<string, string> = {};
        for (const code of codes) {
            const text = (map[code] || '').trim();
            if (text) ordered[code] = text;
        }
        return Object.keys(ordered).length > 0 ? JSON.stringify(ordered) : '';
    };

    const diagnosisTextMap = parseDiagnosisTextMap(formData.diagnosis_text);

    const handleAddDiagnosis = (code: string) => {
        if (!formData.diagnosis.includes(code)) {
            handleFormChange('diagnosis', [...formData.diagnosis, code]);
        }
        setDiagnosisSearch('');
        setShowDiagnosisDropdown(false);
    };

    const handleRemoveDiagnosis = (code: string) => {
        const newDiagnosis = formData.diagnosis.filter((d) => d !== code);
        const { [code]: _removed, ...restMap } = parseDiagnosisTextMap(formData.diagnosis_text);
        setFormData((prev) => ({
            ...prev,
            diagnosis: newDiagnosis,
            diagnosis_text: serializeNotes(newDiagnosis, restMap),
        }));
        setHasUnsavedChanges(true);
        if (editingNoteCode === code) {
            setEditingNoteCode(null);
            setNoteDraft('');
        }
    };

    const handleStartEditNote = (code: string) => {
        if (!isEditable) return;
        setNoteDraft(parseDiagnosisTextMap(formData.diagnosis_text)[code] || '');
        setEditingNoteCode(code);
    };

    const handleSaveNote = (code: string) => {
        const text = noteDraft.trim();
        const map = parseDiagnosisTextMap(formData.diagnosis_text);
        if (text) map[code] = text; else delete map[code];
        handleFormChange('diagnosis_text', serializeNotes(formData.diagnosis, map));
        setEditingNoteCode(null);
        setNoteDraft('');
    };

    const handleRemoveNote = (code: string) => {
        const { [code]: _removed, ...restMap } = parseDiagnosisTextMap(formData.diagnosis_text);
        handleFormChange('diagnosis_text', serializeNotes(formData.diagnosis, restMap));
    };

    const getFilteredDiagnosisCodes = () => {
        if (!diagnosisSearch) return diagnosisCodes.slice(0, 50);
        const search = diagnosisSearch.toLowerCase();
        return diagnosisCodes
            .filter((d) =>
                d.code.toLowerCase().includes(search)
                || d.Diagnosis.toLowerCase().includes(search)
            )
            .slice(0, 50);
    };

    // --- files ---------------------------------------------------------------------

    const openFile = async (file: MedicalOpinionFile) => {
        setSelectedFileId(file.file_id);
        setPreviewName(file.filename);
        setPreviewType(file.content_type);
        setPreviewError(null);
        setIsPreviewLoading(true);
        setPreview(null);
        try {
            const result = await apiService.getMedicalOpinionFile({
                token,
                patient_id: opinion.patient_id,
                medical_opinion_id: opinion.id,
                file_id: file.file_id,
            });
            if (result?.status === 'error' || !result?.base64file) {
                setPreviewError(result?.message || 'File could not be loaded');
                return;
            }
            setPreview(base64ToObjectUrl(result.base64file, result.content_type || file.content_type));
            setPreviewType(result.content_type || file.content_type);
        } catch (err: any) {
            setPreviewError(
                err?.response?.status === 403
                    ? 'You are not authorized to open this file'
                    : 'File could not be loaded'
            );
        } finally {
            setIsPreviewLoading(false);
        }
    };

    // Clicking the preview icon again closes the pane.
    const handleSelectFile = async (file: MedicalOpinionFile) => {
        if (selectedFileId === file.file_id) {
            clearPreview();
            return;
        }
        await openFile(file);
    };

    // Fetched independently of the preview: downloading the letter is what the doctor does
    // before pressing Update In Clicks, and making them open it first would be a step for
    // nothing.
    const handleDownloadFile = async (file: MedicalOpinionFile) => {
        setDownloadingFileId(file.file_id);
        setError(null);
        try {
            const result = await apiService.getMedicalOpinionFile({
                token,
                patient_id: opinion.patient_id,
                medical_opinion_id: opinion.id,
                file_id: file.file_id,
            });
            if (result?.status === 'error' || !result?.base64file) {
                setError(result?.message || 'File could not be downloaded');
                return;
            }
            downloadBase64File(
                result.base64file,
                result.content_type || file.content_type,
                result.filename || file.filename
            );
        } catch (err: any) {
            setError(
                err?.response?.status === 403
                    ? 'You are not authorized to open this file'
                    : 'File could not be downloaded'
            );
        } finally {
            setDownloadingFileId(null);
        }
    };

    // --- actions -------------------------------------------------------------------

    const contentPayload = () => ({
        token,
        patient_id: opinion.patient_id,
        medical_opinion_id: opinion.id,
        summary_and_discussion: formData.summary_and_discussion,
        status: formData.status,
        recommendations: formData.recommendations,
        diagnosis: formData.diagnosis.join(','),
        diagnosis_text: formData.diagnosis_text,
    });

    const authError = (err: any, fallback: string) =>
        err?.response?.status === 403
            ? 'You are not authorized to write this letter'
            : err?.message || fallback;

    // Uploaded one at a time, as the route takes them: a file the backend rejects reports
    // against itself and the rest still land. Read as base64 here because that is what the
    // whole feature's upload path expects - see CreateMedicalOpinionModal.
    const handleAddFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const picked = Array.from(e.target.files || []);
        // Cleared so re-picking the same file after a failure still fires onChange.
        e.target.value = '';
        if (!picked.length) return;

        setError(null);
        setNotice(null);
        setIsUploading(true);
        try {
            let room = MAX_FILES - otherFiles.length;
            for (const file of picked) {
                if (room <= 0) {
                    setError(`No more than ${MAX_FILES} additional files per request`);
                    break;
                }
                if (file.size > MAX_FILE_BYTES) {
                    setError(`"${file.name}" is larger than ${MAX_FILE_BYTES / (1024 * 1024)}MB`);
                    continue;
                }
                // Some browsers report an empty type; let those through and leave the real
                // check to the backend's content sniffing.
                if (file.type && !ACCEPTED_MIME.includes(file.type)) {
                    setError(`"${file.name}" must be a PDF, JPEG or PNG`);
                    continue;
                }

                const base64file = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    // readAsDataURL gives "data:<mime>;base64,<payload>" - the route wants
                    // the payload alone.
                    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
                    reader.onerror = () => reject(reader.error);
                    reader.readAsDataURL(file);
                });

                const result = await apiService.addMedicalOpinionFile({
                    token,
                    patient_id: opinion.patient_id,
                    medical_opinion_id: opinion.id,
                    filename: file.name,
                    base64file,
                });
                if (result?.status === 'error') {
                    setError(result.message || `"${file.name}" could not be uploaded`);
                    continue;
                }
                if (result.file) {
                    setFiles((prev) => [...prev, result.file as MedicalOpinionFile]);
                    room -= 1;
                }
            }
        } catch (err: any) {
            setError(authError(err, 'The file could not be uploaded'));
        } finally {
            setIsUploading(false);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        setError(null);
        setNotice(null);
        try {
            const result = await apiService.medicalOpinionPsyResponse({
                ...contentPayload(),
                is_completed: false,
            });
            if (result?.status === 'error') {
                setError(result.message || 'Failed to save the letter');
                return;
            }
            setHasUnsavedChanges(false);
            setNotice('Saved');
        } catch (err: any) {
            setError(authError(err, 'Failed to save the letter'));
        } finally {
            setIsSaving(false);
        }
    };

    // Saves and renders in one call, then locks. The request is refetched afterwards rather
    // than patched locally: the file list and the lock both change, and the record is the
    // source of truth for each.
    const handleCreatePdf = async () => {
        setIsSaving(true);
        setError(null);
        setNotice(null);
        try {
            const result = await apiService.postMedicalOpinionPdf(contentPayload());
            if (result?.status === 'error') {
                setError(result.message || 'Failed to create the PDF');
                return;
            }
            setHasUnsavedChanges(false);
            setIsLocked(true);
            setNotice('PDF created.');

            try {
                const refreshed = await apiService.getMedicalOpinion({
                    token,
                    medical_opinion_id: opinion.id,
                });
                setFiles(refreshed?.files || []);
                setIsLocked(!!refreshed?.summary_locked);
            } catch {
                // The letter is stored either way; only the file list is stale, and the
                // returned row is enough to show it.
                if (result.file) setFiles((prev) => [
                    ...prev.filter((f) => f.file_type !== 'output_summary'),
                    result.file as MedicalOpinionFile,
                ]);
            }

            // Straight into the preview: the doctor's next step is to download this file and
            // put it into Clicks.
            if (result.file) await openFile(result.file);
        } catch (err: any) {
            setError(authError(err, 'Failed to create the PDF'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleUnlock = async () => {
        setIsSaving(true);
        setError(null);
        setNotice(null);
        try {
            const result = await apiService.medicalOpinionUnlock({
                token,
                patient_id: opinion.patient_id,
                medical_opinion_id: opinion.id,
            });
            if (result?.status === 'error') {
                setError(result.message || 'Failed to unlock the letter');
                return;
            }
            setIsLocked(false);
            // The backend discards the letter along with the lock - it has to be generated
            // again anyway, and a stale one is the file a doctor might send to Clicks.
            setFiles((prev) => prev.filter((f) => f.file_type !== 'output_summary'));
            if (letterFile && selectedFileId === letterFile.file_id) clearPreview();
            setNotice('Reopened for editing. The PDF was discarded — create it again before updating Clicks.');
        } catch (err: any) {
            setError(authError(err, 'Failed to unlock the letter'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleComplete = async () => {
        setIsSaving(true);
        setError(null);
        setNotice(null);
        try {
            const result = await apiService.medicalOpinionPsyResponse({
                ...contentPayload(),
                is_completed: true,
            });
            if (result?.status === 'error') {
                setError(result.message || 'Failed to complete the request');
                return;
            }
            setIsConfirmingComplete(false);
            onSuccess();
            onClose();
        } catch (err: any) {
            setError(authError(err, 'Failed to complete the request'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleRequestClose = () => {
        if (hasUnsavedChanges) {
            setIsConfirmingDiscard(true);
            return;
        }
        onClose();
    };

    const handleDeleteFile = async (file: MedicalOpinionFile) => {
        setDeletingFileId(file.file_id);
        setError(null);
        setNotice(null);
        try {
            const result = await apiService.deleteMedicalOpinionFile({
                token,
                patient_id: opinion.patient_id,
                medical_opinion_id: opinion.id,
                file_id: file.file_id,
            });
            if (result?.status === 'error') {
                setError(result.message || 'The file could not be removed');
                return;
            }
            setFiles((prev) => prev.filter((f) => f.file_id !== file.file_id));
            // The pane would otherwise keep showing a file that no longer exists.
            if (selectedFileId === file.file_id) clearPreview();
        } catch (err: any) {
            setError(authError(err, 'The file could not be removed'));
        } finally {
            setDeletingFileId(null);
        }
    };

    // --- rendering -----------------------------------------------------------------

    const copyButton = (label: string, value: string) => (
        <button
            data-ai-id="opinion-letter-copy-field"
            onClick={() => copy(label, value)}
            disabled={!value}
            className="text-[#366A75] hover:text-[#2D5A60] disabled:opacity-40 align-middle"
            title={copied === label ? 'Copied' : `Copy ${label}`}
        >
            {copied === label ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
            ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
            )}
        </button>
    );

    const fileList = (list: MedicalOpinionFile[], emptyText: string, canRemove = false) => (
        list.length === 0 ? (
            <p className="text-sm text-gray-500">{emptyText}</p>
        ) : (
            <ul className="space-y-1">
                {list.map((file) => (
                    <MedicalOpinionFileRow
                        key={file.id}
                        filename={file.filename}
                        isSelected={selectedFileId === file.file_id}
                        onPreview={() => handleSelectFile(file)}
                        onDownload={() => handleDownloadFile(file)}
                        isDownloading={downloadingFileId === file.file_id}
                        onRemove={canRemove ? () => handleDeleteFile(file) : undefined}
                        disabled={deletingFileId === file.file_id}
                    />
                ))}
            </ul>
        )
    );

    // The clinical fields are editable only for the assigned doctor, on an in-progress
    // request, while it is unlocked - which is why Create PDF visibly changes the whole tab:
    // it sets the lock, and every field re-renders in the locked style.
    //
    // The two states are drawn as different things rather than one box greyed out. Editable
    // is white - the lightest surface in the palette, the one that invites typing - with the
    // field border and focus ring. Locked is a duller #D0E7E5 block with no border: still
    // clearly delimited against the modal's own #B4D9D4, but plainly not a place to type. It
    // also grows to its content instead of scrolling inside the body's scroll.
    const clinicalField = (
        label: string,
        field: 'summary_and_discussion' | 'status' | 'recommendations',
        rows: number
    ) => (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
            {isEditable ? (
                <textarea
                    value={formData[field]}
                    onChange={(e) => handleFormChange(field, e.target.value)}
                    rows={rows}
                    dir="rtl"
                    className="w-full px-4 py-2 rounded-lg bg-white border-[1px] border-[#B4D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent resize-y"
                    disabled={isSaving}
                />
            ) : (
                <div
                    dir="rtl"
                    className="w-full px-4 py-2 rounded-lg bg-[#D0E7E5] text-[#366A75] whitespace-pre-wrap break-words min-h-[2.5rem]"
                >
                    {formData[field] || <span className="text-[#366A75]/50">—</span>}
                </div>
            )}
        </div>
    );

    const tabButton = (tab: 'request' | 'opinion', label: string) => (
        <button
            data-ai-id="opinion-letter-tab"
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-1.5 text-sm rounded-t-lg transition-colors ${
                activeTab === tab
                    ? 'bg-[#EBF5F5] text-[#2D5A60] font-semibold'
                    : 'text-[#366A75] hover:bg-[#D0E7E5]'
            }`}
        >
            {label}
        </button>
    );

    return (
        <>
        <ModalWrapper modalId="MedicalOpinionLetterModal"
            isOpen={isOpen}
            onClose={handleRequestClose}
            title={title}
            isLoading={isSaving}
            // max-w-none: the size comes from the style below, so no class may cap it.
            width="max-w-none"
            // Never taller than the viewport: the letter is long, so the modal is a column
            // whose middle section scrolls and whose header, tabs and buttons do not.
            className="max-h-[90vh] flex flex-col"
            style={{
                width: modalWidth,
                // Grows sideways to make room for the preview rather than resizing the form,
                // same as the obligation modal - and the user can drag it from the
                // bottom-right corner. overflow is what makes `resize` work at all.
                resize: 'both',
                overflow: 'hidden',
                maxWidth: '96vw',
                minWidth: '28rem',
                minHeight: '20rem',
            }}
        >
            <div className="flex gap-6 items-stretch flex-1 min-h-0">
                <div className="flex flex-col min-h-0 flex-1 min-w-0">
                    {/* Header - the obligation modal's block, without the close reason */}
                    <div className="flex gap-x-6 text-base text-[#366A75] mb-4 shrink-0">
                        <div className="flex flex-col gap-y-1 flex-1">
                            <div><span className="font-bold">Name:</span> {patientName || '—'}</div>
                            <div className="flex items-center gap-1">
                                <span><span className="font-bold">Id (TZ):</span> {patientTz || '—'}</span>
                                {copyButton('Id (TZ)', patientTz)}
                            </div>
                            <div className="flex items-center gap-1">
                                <span><span className="font-bold">HM_ID:</span> {patientHmId || '—'}</span>
                                {copyButton('HM_ID', patientHmId)}
                            </div>
                            <div className="flex items-center gap-1">
                                <span>
                                    <span className="font-bold">Phone:</span>{' '}
                                    {displayPhoneNumber(patientPhone) || '—'}
                                </span>
                                <button
                                    onClick={() => setIsCallModalOpen(true)}
                                    // Any number on record is enough: a youth patient often
                                    // has none of their own and both parents' in the picker.
                                    disabled={!patientPhone && !parent1Phone && !parent2Phone}
                                    className="text-[#366A75] hover:text-[#2D5A60] disabled:opacity-40 align-middle"
                                    title="Call patient"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                </button>
                            </div>
                            <div><span className="font-bold">Clinic:</span> {clinicName || '—'}</div>
                            <div><span className="font-bold">Doctor:</span> {doctorName || '—'}</div>
                        </div>
                        <div className="flex flex-col gap-y-1 flex-1">
                            <div>
                                <span className="font-bold">Opinion Type:</span>{' '}
                                {opinionTypeLabels[opinion.opinion_type] || opinion.opinion_type}
                            </div>
                            <div><span className="font-bold">Requested By:</span> {requestedByName}</div>
                            <div><span className="font-bold">Requested At:</span> {formatDateTime(opinion.created_at)}</div>
                            <div>
                                <span className="font-bold">Status:</span>{' '}
                                {opinion.heb_status || opinion.status}
                                {isLocked && opinion.status === 'in_progress' && (
                                    <span className="ml-2 text-sm">(locked)</span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-1 border-b border-[#9BC9C3] shrink-0">
                        {tabButton('request', 'Request')}
                        {tabButton('opinion', 'Opinion')}
                    </div>

                    {/* Outside the scroll region on purpose: this is the instruction the doctor
                        is acting on, and the file it refers to is at the bottom of a long tab. */}
                    {activeTab === 'opinion' && !isLoading && isLocked && canAct && (
                        <div className={`${POPUP_STYLES.note} mt-4 shrink-0`}>
                            Opinion pdf was created (see below). Review the file and upload it to
                            Clicks, and then click on Update In Clicks to finalize handling. If you
                            need to revise the content press below on Reopen for Edit.
                        </div>
                    )}

                    {/* The scrolling body: everything from the tab content down to the files
                        section. The buttons below it stay visible however long the letter is. */}
                    <div className="flex-1 min-h-0 overflow-y-auto pt-4 pr-1">
                    {activeTab === 'request' && (
                        <div className="space-y-4 mb-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Request Details
                                </label>
                                <textarea
                                    value={opinion.initiator_notes || ''}
                                    rows={3}
                                    readOnly
                                    dir={getTextDirection(opinion.initiator_notes)}
                                    className="w-full px-4 py-2 rounded-lg bg-[#D0E7E5] text-[#366A75] resize-none focus:outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Obligation Number
                                </label>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        value={opinion.obligation ? String(opinion.obligation) : ''}
                                        readOnly
                                        className="w-full px-4 py-2 rounded-lg bg-[#D0E7E5] text-[#366A75] focus:outline-none"
                                    />
                                    {copyButton('Obligation', opinion.obligation ? String(opinion.obligation) : '')}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Request Files
                                </label>
                                {fileList(inputFiles, 'No files attached to this request')}
                            </div>
                        </div>
                    )}

                    {activeTab === 'opinion' && (
                        <div className="space-y-4 mb-6">
                            {isLoading ? (
                                <div className="flex justify-center py-10">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#366A75]"></div>
                                </div>
                            ) : (
                                <>
                                    {clinicalField('Summary and Discussion', 'summary_and_discussion', 6)}

                                    {/* Diagnosis - the summary modal's picker, on the same
                                        code list and the same per-code note map */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                            Diagnosis
                                        </label>

                                        {formData.diagnosis.length > 0 && (
                                            <div className="flex flex-col gap-2 mb-2">
                                                {formData.diagnosis.map((code) => {
                                                    const diagnosisItem = diagnosisCodes.find((d) => d.code === code);
                                                    const note = diagnosisTextMap[code] || '';
                                                    const isEditingNote = editingNoteCode === code;
                                                    return (
                                                        <div key={code} className="flex flex-col gap-1">
                                                            <div className="flex items-center gap-2">
                                                                <span className={`inline-flex items-stretch text-xs rounded-lg overflow-hidden flex-shrink-0 ${
                                                                    isEditable ? 'border-[1px] border-[#B4D9D4]' : ''
                                                                }`}>
                                                                    <button
                                                                        data-ai-id="opinion-letter-edit-note"
                                                                        type="button"
                                                                        onClick={() => handleStartEditNote(code)}
                                                                        title={isEditable ? 'Click to add or edit a note' : undefined}
                                                                        className={`px-2 py-1 text-left transition-colors ${
                                                                            isEditable
                                                                                ? 'bg-white text-gray-900 hover:bg-[#EBF5F5]'
                                                                                : 'bg-[#D0E7E5] text-[#366A75]'
                                                                        }`}
                                                                    >
                                                                        {code}
                                                                        {diagnosisItem && ` - ${diagnosisItem.Diagnosis}`}
                                                                    </button>
                                                                    {note && !isEditingNote && (
                                                                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-gray-700 ${
                                                                            isEditable
                                                                                ? 'bg-[#EBF5F5] border-l border-[#B4D9D4]'
                                                                                : 'bg-[#C2DCD9]'
                                                                        }`}>
                                                                            {note}
                                                                            {isEditable && (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => handleRemoveNote(code)}
                                                                                    title="Remove note"
                                                                                    className="ml-1 text-gray-500 hover:text-red-600"
                                                                                >
                                                                                    ✕
                                                                                </button>
                                                                            )}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                                {isEditable && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleRemoveDiagnosis(code)}
                                                                        title="Remove diagnosis"
                                                                        className="text-gray-500 hover:text-red-600 flex-shrink-0"
                                                                    >
                                                                        ✕
                                                                    </button>
                                                                )}
                                                            </div>

                                                            {isEditingNote && (
                                                                <div className="flex flex-col gap-2">
                                                                    <textarea
                                                                        autoFocus
                                                                        value={noteDraft}
                                                                        onChange={(e) => setNoteDraft(e.target.value)}
                                                                        dir="rtl"
                                                                        rows={3}
                                                                        placeholder="Add notes for this diagnosis..."
                                                                        className="w-full px-3 py-2 border-[1px] border-[#366A75] rounded bg-white text-xs focus:outline-none focus:ring-2 focus:ring-[#366A75] resize-y"
                                                                    />
                                                                    <div className="flex items-center gap-2">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleSaveNote(code)}
                                                                            className="px-3 py-1 text-xs bg-[#EBF5F5] text-[#3A7380] rounded hover:bg-[#D0E7E5] transition-colors"
                                                                        >
                                                                            Set Note
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                setEditingNoteCode(null);
                                                                                setNoteDraft('');
                                                                            }}
                                                                            className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                                                                        >
                                                                            Cancel
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        {formData.diagnosis.length === 0 && !isEditable && (
                                            <p className="text-sm text-gray-500 mb-2">No diagnosis recorded</p>
                                        )}

                                        {isEditable && (
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    placeholder="Search diagnosis codes..."
                                                    value={diagnosisSearch}
                                                    onChange={(e) => setDiagnosisSearch(e.target.value)}
                                                    onFocus={() => setShowDiagnosisDropdown(true)}
                                                    className="w-full px-4 py-2 rounded-lg bg-white border-[1px] border-[#B4D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent"
                                                />
                                                {showDiagnosisDropdown && diagnosisSearch && (
                                                    <>
                                                        <div
                                                            className="fixed inset-0 z-10"
                                                            onClick={() => {
                                                                setShowDiagnosisDropdown(false);
                                                                setDiagnosisSearch('');
                                                            }}
                                                        />
                                                        <div className="absolute z-20 w-full mt-1 bg-white border-[1px] border-[#9BC9C3] rounded-lg shadow-lg max-h-64 overflow-y-auto">
                                                            {getFilteredDiagnosisCodes().length > 0 ? (
                                                                getFilteredDiagnosisCodes().map((diagnosisItem) => (
                                                                    <div
                                                                        key={diagnosisItem.code}
                                                                        onClick={() => handleAddDiagnosis(diagnosisItem.code)}
                                                                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-200 last:border-b-0"
                                                                    >
                                                                        <div className="text-sm font-medium text-gray-900">
                                                                            {diagnosisItem.code}
                                                                        </div>
                                                                        <div className="text-xs text-gray-600">
                                                                            {diagnosisItem.Diagnosis}
                                                                        </div>
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <div className="px-3 py-2 text-sm text-gray-500">
                                                                    No diagnosis codes found
                                                                </div>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Medications - read-only everywhere. They are never
                                        stored against the request: the letter shows whatever
                                        is prescribed at the last psychiatric meeting, and the
                                        generated PDF freezes that. */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                            Medications
                                        </label>
                                        {medications.length === 0 ? (
                                            <p className="text-sm text-gray-500">No medications prescribed</p>
                                        ) : (
                                            <ul className="space-y-1">
                                                {medications.map((medication, index) => (
                                                    <li
                                                        key={medication.id ?? index}
                                                        // Never editable here, so always the
                                                        // locked tone.
                                                        className="text-sm rounded-lg px-3 py-1.5 bg-[#D0E7E5] text-[#366A75]"
                                                    >
                                                        {formatMedication(medication, medicationTypes)}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>

                                    {clinicalField('Status', 'status', 4)}
                                    {clinicalField('Recommendations', 'recommendations', 4)}

                                    {/* The letter has a fixed slot rather than a place in a
                                        list: there is at most one, its absence is worth
                                        stating, and it must not be lost among attachments. */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                            Psychiatric Opinion PDF
                                        </label>
                                        {letterFile ? (
                                            <ul>
                                                <MedicalOpinionFileRow
                                                    filename={letterFile.filename}
                                                    isSelected={selectedFileId === letterFile.file_id}
                                                    onPreview={() => handleSelectFile(letterFile)}
                                                    onDownload={() => handleDownloadFile(letterFile)}
                                                    isDownloading={downloadingFileId === letterFile.file_id}
                                                />
                                            </ul>
                                        ) : (
                                            <div className="text-sm rounded-lg px-3 py-1 bg-[#D0E7E5] text-[#366A75]/70">
                                                PDF not created yet
                                            </div>
                                        )}
                                    </div>

                                    {/* Anything else the psychiatrist sends back. Uploaded the
                                        same way the request's own files were, and only while
                                        the letter is open for editing - the backend refuses
                                        once it is locked. */}
                                    <div>
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <span className="text-sm font-medium text-gray-700">
                                                Other Files
                                                {isEditable && (
                                                    <span className="text-xs font-normal text-gray-500">
                                                        {' '}(up to {MAX_FILES} files, PDF / JPEG / PNG,
                                                        max {MAX_FILE_BYTES / (1024 * 1024)}MB each)
                                                    </span>
                                                )}
                                            </span>
                                            {isEditable && (
                                                <>
                                                    {/* A label pointing at a disabled input does
                                                        not open the file dialog, so the limit is
                                                        enforced by disabling the input itself. */}
                                                    <input
                                                        type="file"
                                                        multiple
                                                        accept={ACCEPT_ATTR}
                                                        onChange={handleAddFiles}
                                                        className="hidden"
                                                        id="medical-opinion-output-files"
                                                        disabled={isSaving || isUploading || otherFiles.length >= MAX_FILES}
                                                    />
                                                    <label
                                                        htmlFor="medical-opinion-output-files"
                                                        className={`shrink-0 rounded-lg border-[1px] border-[#B4D9D4] bg-[#EBF5F5] text-[#366A75] p-1 leading-none ${
                                                            isSaving || isUploading || otherFiles.length >= MAX_FILES
                                                                ? 'opacity-50 cursor-not-allowed'
                                                                : 'cursor-pointer hover:bg-[#D0E7E5]'
                                                        }`}
                                                        title={
                                                            otherFiles.length >= MAX_FILES
                                                                ? `${MAX_FILES} of ${MAX_FILES} files added`
                                                                : 'Add files'
                                                        }
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                        </svg>
                                                    </label>
                                                </>
                                            )}
                                            {isUploading && (
                                                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#366A75]" />
                                            )}
                                        </div>
                                        {fileList(otherFiles, 'No additional files', isEditable)}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    </div>

                    <div className="shrink-0 pt-4">
                    {notice && (
                        <div className={`${POPUP_STYLES.note} mb-4`}>
                            {notice}
                        </div>
                    )}

                    {error && (
                        <div className="mb-4 p-3 bg-red-100 border-[1px] border-red-400 text-red-700 rounded-lg text-sm">
                            {error}
                        </div>
                    )}

                    {isConfirmingComplete && (
                        <div className={`${POPUP_STYLES.note} mb-4`}>
                            <p className="mb-2">
                                This completes the request and tells the patient the document is ready.
                                Have you already uploaded the letter to Clicks?
                            </p>
                            <div className="flex gap-3">
                                <button onClick={handleComplete} disabled={isSaving} className={POPUP_STYLES.button}>
                                    {isSaving ? 'Updating...' : 'Yes, complete'}
                                </button>
                                <button
                                    onClick={() => setIsConfirmingComplete(false)}
                                    disabled={isSaving}
                                    className={POPUP_STYLES.button}
                                >
                                    Not yet
                                </button>
                            </div>
                        </div>
                    )}

                    {isConfirmingDiscard && (
                        <div className={`${POPUP_STYLES.note} mb-4`}>
                            <p className="mb-2">The letter has unsaved changes. Close without saving?</p>
                            <div className="flex gap-3">
                                <button onClick={onClose} className={POPUP_STYLES.button}>
                                    Discard changes
                                </button>
                                <button onClick={() => setIsConfirmingDiscard(false)} className={POPUP_STYLES.button}>
                                    Keep editing
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-center flex-wrap gap-4">
                        <button onClick={handleRequestClose} disabled={isSaving} className={POPUP_STYLES.button}>
                            {/* Nothing to cancel when there is nothing to save: a completed
                                request, and a read-only viewer of any other status. */}
                            {canAct ? 'Cancel' : 'Close'}
                        </button>
                        {canAct && !isLocked && (
                            <>
                                <button onClick={handleSave} disabled={isSaving || isLoading} className={POPUP_STYLES.button}>
                                    {isSaving ? 'Saving...' : 'Save'}
                                </button>
                                <button onClick={handleCreatePdf} disabled={isSaving || isLoading} className={POPUP_STYLES.button}>
                                    Create PDF
                                </button>
                            </>
                        )}
                        {canAct && isLocked && (
                            <>
                                <button onClick={handleUnlock} disabled={isSaving} className={POPUP_STYLES.button}>
                                    Reopen for Edit
                                </button>
                                <button
                                    onClick={() => setIsConfirmingComplete(true)}
                                    disabled={isSaving || isConfirmingComplete}
                                    className={POPUP_STYLES.button}
                                >
                                    Update In Clicks
                                </button>
                            </>
                        )}
                    </div>
                    </div>
                </div>

                {/* Right side - file preview, shared by both tabs. Clicking the same file
                    again closes it, which is why there is no close control here. */}
                {isPreviewOpen && (
                    <div className="flex-1 min-w-0 flex flex-col border-l border-gray-200 pl-6">
                        <MedicalOpinionFilePreview
                            src={previewUrl}
                            contentType={previewType}
                            filename={previewName}
                            isLoading={isPreviewLoading}
                            error={previewError}
                            heightClass="flex-1 min-h-0"
                        />
                    </div>
                )}
            </div>
        </ModalWrapper>

        {/* After the ModalWrapper, not before it: both use the same z-index, so a call modal
            rendered first would sit behind this one. */}
        {isCallModalOpen && (
            <CallPatientConfModal
                patientId={opinion.patient_id}
                patientName={patientName}
                patientPhone={patientPhone}
                parent1Phone={parent1Phone}
                parent2Phone={parent2Phone}
                token={token}
                onClose={() => setIsCallModalOpen(false)}
                onSuccess={() => {
                    setCallMessage('Call initiated successfully!');
                    setTimeout(() => setCallMessage(null), 3000);
                }}
                onError={(msg) => {
                    setCallMessage(msg);
                    setTimeout(() => setCallMessage(null), 3000);
                }}
            />
        )}

        {callMessage && (
            <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[1100] px-6 py-3 rounded-lg shadow-lg bg-white text-sm text-[#366A75]">
                {callMessage}
            </div>
        )}
        </>
    );
};
